import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import Section from "../../../ui/forms/Section";
import type { ProcessSearchItem, SearchKind } from "./processSearchApi";
import { similarQuery, watchSimilarItems, type SimilarSearchState } from "./similarSearch";
import UseInWorkflow from "./UseInWorkflow";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { itemKindText } from "../../workflows/inputCards/typeWords";

interface SimilarItemsPanelProps {
  projectId: string;
  kind: SearchKind;
  name: string;
  description?: string;
  excludeItemId?: string;
}

/** Mount a fresh search for each input so old results disappear before the debounce. */
export default function SimilarItemsPanel({ projectId, kind, name, description = "", excludeItemId }: SimilarItemsPanelProps) {
  const query = similarQuery(name, description);
  if (query === "") return null;
  return <SimilarSearch key={JSON.stringify([projectId, kind, query])} projectId={projectId} kind={kind} query={query} excludeItemId={excludeItemId} />;
}

function SimilarSearch({ projectId, kind, query, excludeItemId }: { projectId: string; kind: SearchKind; query: string; excludeItemId: string | undefined }) {
  const { t } = useTranslation();
  const [state, setState] = useState<SimilarSearchState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [using, setUsing] = useState<ProcessSearchItem | null>(null);
  useEffect(() => watchSimilarItems(projectId, query, kind === "step" ? ["step", "workflow"] : [kind], setState), [projectId, query, kind, attempt]);
  const items = state.status === "loaded" ? state.value.filter((item) => item.itemId !== excludeItemId) : [];
  return (
    <Section title={t("authoring.similar.title")}>
      <LoadStatus state={state} loadingLabel={t("authoring.similar.loading")} onRetry={() => { setState({ status: "loading" }); setAttempt(attempt + 1); }} compact />
      {state.status === "loaded" && items.length === 0 ? <p className="app-text-muted text-xs" role="status">{t("authoring.similar.empty")}</p> : null}
      <ul className="space-y-3" aria-live="polite">
        {items.map((item) => (
          <li key={item.reference} className="space-y-1 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="app-title font-semibold">{item.name}</span>
              <span className="app-text-muted">{itemKindText(item.kind, t)}</span>
            </div>
            {item.description === "" ? null : <p className="app-text-muted line-clamp-2">{item.description}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <Link className="app-link underline" to={item.kind === "workflow" ? appPaths.workflow(item.reference) : appPaths.item(item.reference)}>{t("authoring.similar.open")}</Link>
              {item.kind === "step" || item.kind === "workflow" ? <AppButton size="xs" variant="ghost" onClick={() => { setUsing(item); }}>{t("authoring.similar.use")}</AppButton> : null}
            </div>
          </li>
        ))}
      </ul>
      {using === null ? null : <UseInWorkflow projectId={projectId} item={using} onClose={() => { setUsing(null); }} />}
    </Section>
  );
}
